import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { CheckCircle2, Lock, Plus, Printer, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { FormSection } from '@/components/forms/Form';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormField, FormSelect, FormTextarea } from '@/components/forms/FormField';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useCaisses, useComptesBancaires } from '@/hooks/queries/useBanques';
import { useSoldes } from '@/hooks/queries/useSoldes';
import {
  useClotures,
  useCreateCloture,
  useDeleteCloture,
  useUpdateClotureStatus,
} from '@/hooks/queries/useClotures';
import { useRole } from '@/hooks/useRole';
import { usePermission } from '@/hooks/usePermission';
import { AccessDenied } from '@/components/AccessDenied';
import { formatDA, formatDateDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';
import type { ClotureRow, ClotureStatut } from '@/lib/database.types';

const STATUT_VARIANT: Record<ClotureStatut, 'outline' | 'warning' | 'success' | 'secondary'> = {
  brouillon: 'outline',
  en_attente: 'warning',
  validee: 'success',
  verrouillee: 'secondary',
};

const STATUT_LABEL: Record<ClotureStatut, string> = {
  brouillon: 'Brouillon',
  en_attente: 'En attente',
  validee: 'Validée',
  verrouillee: 'Verrouillée',
};

export function CloturePage(): JSX.Element {
  const { isAdmin, role } = useRole();
  const canView = usePermission('cloture.view');
  const { data: entreprises = [] } = useEntreprises();
  const [entrepriseId, setEntrepriseId] = useState('');
  useEffect(() => {
    if (!entrepriseId && entreprises[0]) setEntrepriseId(entreprises[0].id);
  }, [entreprises, entrepriseId]);

  const { data: clotures = [] } = useClotures(entrepriseId);
  const updateStatus = useUpdateClotureStatus();
  const remove = useDeleteCloture();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<ClotureRow | null>(null);

  const canValidate = role === 'direction' || isAdmin;

  if (!canView) return <AccessDenied feature="cloture.view" />;

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title={t('finances.cloture.title')}
        subtitle={t('finances.cloture.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <select
              value={entrepriseId}
              onChange={(e) => setEntrepriseId(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('finances.cloture.addCloture')}
            </Button>
          </div>
        }
      />

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Compte / caisse</th>
              <th className="px-3 py-2 text-right">{t('finances.cloture.soldeTheo')}</th>
              <th className="px-3 py-2 text-right">{t('finances.cloture.soldeReel')}</th>
              <th className="px-3 py-2 text-right">{t('finances.cloture.ecart')}</th>
              <th className="px-3 py-2 text-left">Statut</th>
              <th className="px-3 py-2 text-right">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {clotures.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                  Aucune clôture pour cette entreprise.
                </td>
              </tr>
            ) : (
              clotures.map((c) => (
                <tr key={c.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                  <td className="px-3 py-2 text-xs">{formatDateDZ(c.date_cloture)}</td>
                  <td className="px-3 py-2 text-xs">
                    {c.caisse_id ? '🏪 Caisse' : '🏦 Banque'}{' '}
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {(c.caisse_id ?? c.compte_bancaire_id ?? '').slice(0, 8)}…
                    </span>
                  </td>
                  <td className="num px-3 py-2 text-right text-xs">
                    {formatDA(c.solde_theorique)}
                  </td>
                  <td className="num px-3 py-2 text-right text-xs">{formatDA(c.solde_reel)}</td>
                  <td
                    className={cn(
                      'num px-3 py-2 text-right text-xs font-medium',
                      Math.abs(c.ecart) < 0.01 && 'text-success',
                      Math.abs(c.ecart) >= 0.01 && 'text-destructive',
                    )}
                  >
                    {formatDA(c.ecart)}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={STATUT_VARIANT[c.statut]}>{STATUT_LABEL[c.statut]}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      {c.statut === 'brouillon' && canValidate && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={async () => {
                            await updateStatus.mutateAsync({ id: c.id, statut: 'validee' });
                            toast.success('Clôture validée');
                          }}
                        >
                          <CheckCircle2 className="h-3 w-3" /> Valider
                        </Button>
                      )}
                      {c.statut === 'validee' && canValidate && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={async () => {
                            await updateStatus.mutateAsync({ id: c.id, statut: 'verrouillee' });
                            toast.success('Clôture verrouillée');
                          }}
                        >
                          <Lock className="h-3 w-3" /> Verrouiller
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() =>
                          printCloture(
                            c,
                            entreprises.find((e) => e.id === c.entreprise_id)?.nom ?? '',
                          )
                        }
                        aria-label="Imprimer"
                      >
                        <Printer className="h-3.5 w-3.5" />
                      </Button>
                      {c.statut === 'brouillon' && isAdmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => setConfirmDelete(c)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <NewClotureDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        entrepriseId={entrepriseId}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title="Supprimer cette clôture ?"
        loading={remove.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await remove.mutateAsync(confirmDelete.id);
          toast.success('Clôture supprimée');
          setConfirmDelete(null);
        }}
      />
    </div>
  );
}

const newSchema = z.object({
  date_cloture: z.string().min(1),
  canal: z.enum(['caisse', 'banque']),
  cible_id: z.string().uuid(),
  solde_theorique: z.coerce.number(),
  solde_reel: z.coerce.number(),
  observation: z.string().nullable().optional(),
});
type NewForm = z.infer<typeof newSchema>;

function NewClotureDialog({
  open,
  onOpenChange,
  entrepriseId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  entrepriseId: string;
}): JSX.Element {
  const create = useCreateCloture();
  const { data: caisses = [] } = useCaisses(entrepriseId);
  const { data: comptes = [] } = useComptesBancaires(entrepriseId);
  const { data: soldes = [] } = useSoldes(entrepriseId);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<NewForm>({
    resolver: zodResolver(newSchema),
    defaultValues: {
      date_cloture: new Date().toISOString().slice(0, 10),
      canal: 'caisse',
      cible_id: '',
      solde_theorique: 0,
      solde_reel: 0,
      observation: '',
    },
  });

  const canal = watch('canal');
  const cibleId = watch('cible_id');

  useEffect(() => {
    if (open) {
      reset({
        date_cloture: new Date().toISOString().slice(0, 10),
        canal: 'caisse',
        cible_id: caisses[0]?.id ?? comptes[0]?.id ?? '',
        solde_theorique: 0,
        solde_reel: 0,
        observation: '',
      });
    }
  }, [open, reset, caisses, comptes]);

  // Auto-fill solde_theorique
  useEffect(() => {
    if (cibleId) {
      const s = soldes.find((x) => x.compte_id === cibleId);
      if (s) setValue('solde_theorique', Number(s.solde_courant.toFixed(2)));
    }
  }, [cibleId, soldes, setValue]);

  const cibleOptions =
    canal === 'caisse'
      ? caisses.map((c) => ({ value: c.id, label: c.nom }))
      : comptes.map((c) => ({ value: c.id, label: c.intitule }));

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={t('finances.cloture.addCloture')}
      isSubmitting={isSubmitting || create.isPending}
      onSubmit={handleSubmit(async (v) => {
        if (!entrepriseId) return;
        await create.mutateAsync({
          entreprise_id: entrepriseId,
          date_cloture: v.date_cloture,
          caisse_id: v.canal === 'caisse' ? v.cible_id : null,
          compte_bancaire_id: v.canal === 'banque' ? v.cible_id : null,
          solde_theorique: v.solde_theorique,
          solde_reel: v.solde_reel,
          observation: v.observation ?? null,
        });
        toast.success('Clôture créée (brouillon)');
        onOpenChange(false);
      })}
    >
      <FormSection>
        <FormField
          type="date"
          label="Date de clôture"
          required
          {...register('date_cloture')}
          error={errors.date_cloture?.message}
        />
        <FormSelect
          label="Type"
          required
          value={watch('canal')}
          onChange={(v) => setValue('canal', v as 'caisse' | 'banque', { shouldValidate: true })}
          options={[
            { value: 'caisse', label: 'Caisse' },
            { value: 'banque', label: 'Compte bancaire' },
          ]}
        />
        <FormSelect
          label="Compte cible"
          required
          value={watch('cible_id')}
          onChange={(v) => setValue('cible_id', v, { shouldValidate: true })}
          options={[{ value: '', label: '— Choisir —' }, ...cibleOptions]}
          fullWidth
        />
        <FormField
          type="number"
          step="0.01"
          label="Solde théorique (auto)"
          required
          {...register('solde_theorique')}
          hint="Calculé d'après les opérations validées"
        />
        <FormField
          type="number"
          step="0.01"
          label="Solde réel (compté)"
          required
          {...register('solde_reel')}
          error={errors.solde_reel?.message}
        />
        <FormTextarea label="Observation" {...register('observation')} fullWidth />
      </FormSection>
    </FormDialog>
  );
}

function printCloture(c: ClotureRow, entrepriseNom: string): void {
  const w = window.open('', '_blank');
  if (!w) return;
  const ecart = Number(c.ecart);
  w.document
    .write(`<!doctype html><html lang="fr-DZ"><head><meta charset="utf-8"><title>Clôture ${c.date_cloture}</title>
  <style>
    body { font-family: -apple-system, system-ui, sans-serif; max-width: 720px; margin: 40px auto; color: #18181b; padding: 0 24px; }
    h1 { font-size: 22px; border-bottom: 2px solid #4338CA; padding-bottom: 12px; }
    table { width: 100%; border-collapse: collapse; margin-top: 24px; }
    td { padding: 8px 0; border-bottom: 1px solid #e4e4e7; }
    .label { color: #71717a; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
    .num { font-family: ui-monospace, monospace; text-align: right; font-weight: 500; }
    .ok { color: #10B981; }
    .ko { color: #E11D48; }
    .footer { margin-top: 60px; display: flex; justify-content: space-between; gap: 40px; }
    .signature { border-top: 1px solid #18181b; padding-top: 8px; font-size: 12px; flex: 1; text-align: center; }
  </style></head><body>
  <h1>Clôture journalière — ${entrepriseNom}</h1>
  <table>
    <tr><td class="label">Date</td><td class="num">${formatDateDZ(c.date_cloture)}</td></tr>
    <tr><td class="label">${c.caisse_id ? 'Caisse' : 'Compte bancaire'}</td><td class="num">${(c.caisse_id ?? c.compte_bancaire_id ?? '').slice(0, 8)}…</td></tr>
    <tr><td class="label">Solde théorique</td><td class="num">${formatDA(c.solde_theorique)}</td></tr>
    <tr><td class="label">Solde réel</td><td class="num">${formatDA(c.solde_reel)}</td></tr>
    <tr><td class="label">Écart</td><td class="num ${Math.abs(ecart) < 0.01 ? 'ok' : 'ko'}">${formatDA(ecart)}</td></tr>
    <tr><td class="label">Statut</td><td class="num">${c.statut}</td></tr>
    ${c.observation ? `<tr><td class="label">Observation</td><td>${c.observation}</td></tr>` : ''}
  </table>
  <div class="footer">
    <div class="signature">Caissier / Comptable</div>
    <div class="signature">Direction</div>
  </div>
  <script>setTimeout(() => window.print(), 300);</script>
  </body></html>`);
  w.document.close();
}
